import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { companyInfo, submitContactForm } from '../utils/mockData';

const Contatti = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success('Richiesta inviata con successo! Ti contatteremo presto.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error('Errore nell\'invio. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-800 to-slate-700">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Contattaci
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Siamo qui per supportarti in ogni fase del tuo progetto
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-20 relative z-10">
            {/* Address */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mx-auto mb-4">
                  <MapPin size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Indirizzo</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {companyInfo.address}
                </p>
              </CardContent>
            </Card>

            {/* Phone */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mx-auto mb-4">
                  <Phone size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Telefono</h3>
                <a href={`tel:${companyInfo.phone}`} className="text-orange-600 hover:text-orange-700 font-semibold block">
                  {companyInfo.phone}
                </a>
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mx-auto mb-4">
                  <Mail size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Email</h3>
                <a href={`mailto:${companyInfo.email}`} className="text-orange-600 hover:text-orange-700 font-semibold block text-sm">
                  {companyInfo.email}
                </a>
              </CardContent>
            </Card>

            {/* Hours */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mx-auto mb-4">
                  <Clock size={28} />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Orari</h3>
                <p className="text-slate-600 text-sm">
                  Lun-Ven: 07:00-12:30<br/>
                  14:00-17:30
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-4">
                Invia una Richiesta
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Compila il form e ti risponderemo nel più breve tempo possibile
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Nome e Cognome *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Mario Rossi"
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="mario@example.com"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefono</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="333 1234567"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Oggetto *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Richiesta preventivo"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Messaggio *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Descrivi la tua richiesta..."
                    className="mt-2"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white"
                >
                  {isSubmitting ? 'Invio in corso...' : (
                    <>
                      <Send className="mr-2" size={20} />
                      Invia Richiesta
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Map & Quick Actions */}
            <div className="space-y-6">
              {/* Map */}
              <Card className="border-none shadow-xl overflow-hidden">
                <div className="h-[400px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.8672892726435!2d16.0789!3d41.3089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDE4JzMyLjAiTiAxNsKwMDQnNDQuMCJF!5e0!3m2!1sit!2sit!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Edil Service 2.0 Location"
                  ></iframe>
                </div>
              </Card>

              {/* Quick Actions */}
              <div className="space-y-4">
                <a 
                  href={companyInfo.location.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white"
                  >
                    <MapPin className="mr-2" size={20} />
                    Apri in Google Maps
                  </Button>
                </a>

                <a 
                  href={`https://wa.me/${companyInfo.whatsappBusiness.replace(/\s/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button 
                    size="lg" 
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    WhatsApp: {companyInfo.phone}
                  </Button>
                </a>

                <a 
                  href={`https://wa.me/${companyInfo.whatsappTech}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button 
                    size="lg" 
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    WhatsApp Tecnico: {companyInfo.whatsappTech}
                  </Button>
                </a>

                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="block"
                >
                  <Button 
                    size="lg" 
                    className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white"
                  >
                    <Phone className="mr-2" size={20} />
                    Chiama: {companyInfo.phone}
                  </Button>
                </a>
              </div>

              {/* Business Hours */}
              <Card className="border-none shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Clock className="text-orange-600" size={24} />
                    Orari di Apertura
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-700 font-medium">Lunedì - Venerdì</span>
                      <span className="text-slate-600">07:00 - 12:30 / 14:00 - 17:30</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700 font-medium">Sabato</span>
                      <span className="text-slate-600">07:00 - 13:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-700 font-medium">Domenica</span>
                      <span className="text-red-600 font-semibold">Chiuso</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
